import axios from '@/lib/axios';
import type { CreateGiveAskPayload, GiveAskItem } from '@/lib/give-ask-api';

export type ConclaveSpeakingState = {
  sessionId: string | null;
  sessionStartedAt: string | null;
  sessionTimerMinutes: number | null;
  userTimerMinutes: number | null;
  serverNow: string;
  speakers: Array<{
    userId: string;
    tableNumber: number | null;
    elapsedMs: number;
    activeStartedAt: string | null;
  }>;
  activeSpeakers?: Array<{
    userId: string;
    tableNumber: number | null;
    name: string;
    image: string | null;
    intro: { title: string | null; body: string | null } | null;
  }>;
};

export type ConclaveConfig = {
  id: string;
  eventId: string;
  tableCount: number | null;
  seatsPerTable: number | null;
  seatsMin: number | null;
  seatsMax: number | null;
  sessionCount: number;
  canSeeOtherUsers: boolean;
  canSeeGivesWithoutContact: boolean;
  seatPreferenceEnabled: boolean;
  includeAllRegistrations: boolean;
  sessionTimerMinutes: number | null;
  userTimerMinutes: number | null;
  dataExpiryAt: string | null;
  matchmakingBrief: string | null;
  matchmakingRubric: unknown;
};

export type ConclaveEvent = {
  id: string;
  title: string;
  eventType?: string | null;
  startDate: string;
  endDate?: string;
  address?: string | null;
  locationType?: string | null;
  isAdmin?: boolean;
  isRegistered?: boolean;
  standalone?: boolean;
  communityId?: string | null;
  slug?: string | null;
};

export type ConclaveBoardPerson = {
  registrationId: string;
  name: string;
  email: string;
  phone: string | null;
  city: string | null;
  organization?: string | null;
  gives: string;
  asks: string;
  dreamReferral: string;
  connectCategories: string[];
  giveBusinessCategories: string[];
  intro: { title: string | null; body: string | null } | null;
  tableNumber: number | null;
};

export type ConclaveBoardResponse = {
  session: { id: string; index: number; status: string } | null;
  config: {
    tableCount: number | null;
    seatsPerTable: number | null;
    seatsMax: number | null;
  };
  tables: Array<{ tableNumber: number; people: ConclaveBoardPerson[] }>;
  unassigned: ConclaveBoardPerson[];
};

export type ConclaveSeatingMember = {
  registrationId: string;
  name: string;
  city: string | null;
  organization: string | null;
  isSelf: boolean;
};

export type ConclaveSeatingOverview = {
  session: { id: string; index: number; status: string } | null;
  myRegistrationId: string;
  myTableNumber: number | null;
  seatsMax: number | null;
  canSeeMembers: boolean;
  tables: Array<{ tableNumber: number; people: ConclaveSeatingMember[] }>;
};

export type EventGiveAskItem = GiveAskItem & {
  enabled: boolean;
  source: 'community' | 'event' | null;
};

const base = (eventId: string) => `/api/events/${eventId}/conclave`;

export const conclaveApi = {
  getConfig: (eventId: string) =>
    axios.get<{ config: ConclaveConfig }>(`${base(eventId)}/config`).then((r) => r.data),
  patchConfig: (eventId: string, body: Partial<ConclaveConfig>) =>
    axios.patch<{ config: ConclaveConfig }>(`${base(eventId)}/config`, body).then((r) => r.data),
  getProfile: (eventId: string) => axios.get(`${base(eventId)}/profile`).then((r) => r.data),
  putProfile: (eventId: string, body: Record<string, unknown>) =>
    axios.put(`${base(eventId)}/profile`, body).then((r) => r.data),
  createIntro: (eventId: string, body: { title: string; body: string }) =>
    axios.post(`${base(eventId)}/intros`, body).then((r) => r.data),
  getSeatPrefs: (eventId: string) =>
    axios.get(`${base(eventId)}/seat-preferences`).then((r) => r.data),
  putSeatPrefs: (eventId: string, toRegistrationIds: string[]) =>
    axios.put(`${base(eventId)}/seat-preferences`, { toRegistrationIds }).then((r) => r.data),
  generateSeating: (eventId: string, mode: 'all' | 'remaining') =>
    axios.post(`${base(eventId)}/seating/generate`, { mode }).then((r) => r.data),
  reshuffleSession: (eventId: string, sessionId: string, dryRun: boolean) =>
    axios
      .post<{
        seated: number;
        tables: number;
        repeatedPairs: number;
        attempts: number;
        sessionIndex: number;
      }>(`${base(eventId)}/seating/generate`, { mode: 'session', sessionId, dryRun })
      .then((r) => r.data),
  getSeatingBoard: (eventId: string, sessionId?: string) =>
    axios
      .get<ConclaveBoardResponse>(`${base(eventId)}/seating/board`, {
        params: sessionId ? { sessionId } : undefined,
      })
      .then((r) => r.data),
  getSeatingOverview: (eventId: string, sessionId?: string) =>
    axios
      .get<ConclaveSeatingOverview>(`${base(eventId)}/seating/overview`, {
        params: sessionId ? { sessionId } : undefined,
      })
      .then((r) => r.data),
  changeMyTable: (eventId: string, body: { sessionId: string; tableNumber: number }) =>
    axios
      .patch<{
        assignment: { tableNumber: number; registrationId: string };
      }>(`${base(eventId)}/me/table`, body)
      .then((r) => r.data),
  overrideAssignment: (
    eventId: string,
    body: { sessionId: string; registrationId: string; tableNumber: number }
  ) => axios.patch(`${base(eventId)}/seating/assignment`, body).then((r) => r.data),
  listEventGiveAsks: (eventId: string) =>
    axios
      .get<{
        communityId: string | null;
        items: EventGiveAskItem[];
      }>(`${base(eventId)}/profile/gives-asks`)
      .then((r) => r.data),
  setEventGiveAsks: (eventId: string, toggles: { giveAskId: string; enabled: boolean }[]) =>
    axios.put(`${base(eventId)}/profile/gives-asks`, { toggles }).then((r) => r.data),
  createEventGiveAsk: (eventId: string, body: CreateGiveAskPayload) =>
    axios.post<{ id: string }>(`${base(eventId)}/profile/gives-asks`, body).then((r) => r.data),
  listSessions: (eventId: string) => axios.get(`${base(eventId)}/sessions`).then((r) => r.data),
  startSession: (eventId: string, sessionId: string) =>
    axios.post(`${base(eventId)}/sessions/${sessionId}/start`).then((r) => r.data),
  completeSession: (eventId: string, sessionId: string) =>
    axios.post(`${base(eventId)}/sessions/${sessionId}/complete`).then((r) => r.data),
  resetSessions: (eventId: string) =>
    axios.post<{ sessions: unknown[] }>(`${base(eventId)}/sessions/reset`).then((r) => r.data),
  myTable: (eventId: string, sessionId?: string) =>
    axios
      .get(`${base(eventId)}/me/table`, { params: sessionId ? { sessionId } : undefined })
      .then((r) => r.data),
  createLead: (
    eventId: string,
    body: {
      toUserId: string;
      title: string;
      requirement: string;
      fulfillers?: string[];
      beneficiary?: 'self' | 'other';
      beneficiaryName?: string;
      beneficiaryContact?: string | null;
      beneficiaryEmail?: string | null;
      beneficiaryRelation?: string | null;
    }
  ) =>
    axios
      .post<{ success: true; leadId: string }>(`${base(eventId)}/leads`, body)
      .then((r) => r.data),
  speaking: (eventId: string, sessionId?: string) =>
    axios
      .get<{ state: ConclaveSpeakingState }>(`${base(eventId)}/speaking`, {
        params: sessionId ? { sessionId } : undefined,
      })
      .then((r) => r.data),
  startSpeaking: (eventId: string) =>
    axios
      .post<{ state: ConclaveSpeakingState }>(`${base(eventId)}/speaking/start`)
      .then((r) => r.data),
  stopSpeaking: (eventId: string) =>
    axios
      .post<{ state: ConclaveSpeakingState }>(`${base(eventId)}/speaking/stop`)
      .then((r) => r.data),
  listNotes: (eventId: string) => axios.get(`${base(eventId)}/notes`).then((r) => r.data),
  createNote: (eventId: string, body: { aboutRegistrationId: string; body: string }) =>
    axios.post(`${base(eventId)}/notes`, body).then((r) => r.data),
  analytics: (eventId: string) => axios.get(`${base(eventId)}/analytics`).then((r) => r.data),
  listConclaveEvents: () =>
    axios.get<{ events: ConclaveEvent[] }>('/api/events/conclave').then((r) => r.data),
  createStandaloneEvent: (body: Record<string, unknown>) =>
    axios.post('/api/events', body).then((r) => r.data),
};
